import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { AgentInfo } from '@api/models/AgentApi/Contract/agent-info';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { AgentService } from '@api/services/agent.service';
import { PushService } from '@core/push/push.service';
import { AddAgentRequest } from '@api/models/AgentApi/Contract/add-agent-request';
import { RouteEntity } from '@core/router/route.entity';
import { PushCodeModel } from '@core/push/push-code.model';
import { Router } from '@angular/router';
import { SetAgentRequest } from '@api/models/AgentApi/Contract/set-agent-request';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { ListAgentDeptResponse } from '@api/models/AgentApi/Contract/AgentDept/list-agent-dept-response';
import { AgentSitesService } from '@api/services/agent-sites.service';
import { AgentData } from '@api/models/AgentApi/Contract/agent-data';
import { SetAgentDeptRequest } from '@api/models/AgentApi/Contract/AgentDept/set-agent-dept-request';
import { tap } from 'rxjs/operators';
import { AddFile } from '@api/models/AgentApi/Contract/add-file';
import { LocalStorage } from '@core/storage/local-storage';
import { StorageKeys } from '@core/storage/storage.keys';
import { LanguageState } from '@core/localization/language.state';
import { CustomRxState } from '@core/state/rx.state';


interface IAgentsState {
    loading: Record<StartWithUnit, boolean>;
    agents: AgentInfo[];
    agentDepartments: ListAgentDeptResponse[];
}

interface AgentsActions {
    requestAgents: AgentInfo[];
    requestAgentDepartments: ListAgentDeptResponse[];
}

@Injectable()
export class AgentsState extends CustomRxState<IAgentsState> {
    @LocalStorage(StorageKeys.Access)
    protected token: string;

    readonly #agentService: AgentService = inject(AgentService);
    readonly #agentSitesService: AgentSitesService = inject(AgentSitesService);
    readonly #push: PushService = inject(PushService);
    readonly #router: Router = inject(Router);
    readonly #languageState: LanguageState = inject(LanguageState);
    readonly #actions: RxActions<AgentsActions> = new RxActionFactory<AgentsActions>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly agents$: Observable<AgentInfo[]> = this.select('agents');
    public readonly agentDepartments$: Observable<ListAgentDeptResponse[]> = this.select('agentDepartments');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    public requestAgents(): void {
        this.hold(
            this.#agentService.apiAgentListAgentGet(),
            this.#actions.requestAgents,
        );
    }

    public requestAgentDepartments(slrId: number): void {
        this.hold(
            this.#agentSitesService.apiAgentListAgentDeptGet({ slrId }),
            this.#actions.requestAgentDepartments,
        );
    }

    public requestAgentData(slrId: number): Observable<AgentData> {
        this.set({ agentDepartments: null });

        return this.#agentService.apiAgentGetAgentDataGet({ slrId });
    }

    public createAgent(body: AddAgentRequest): void {
        this.hold(
            this.#agentService.apiAgentAddAgentPost({ body }).pipe(
                startWithLoading(this, StartWithUnit.RequestAgentAdd),
            ),
            () =>
                this.#router
                    .navigate([RouteEntity.Root, RouteEntity.Agents])
                    .then(() => this.#push.success(PushCodeModel.SuccessAdd)),
        );
    }

    public updateAgent(body: SetAgentRequest): void {
        this.hold(
            this.#agentService.apiAgentSetAgentPost({ body }).pipe(
                startWithLoading(this, StartWithUnit.RequestAgentAdd),
            ),
            () =>
                this.#router
                    .navigate([RouteEntity.Root, RouteEntity.Agents])
                    .then(() => this.#push.success(PushCodeModel.SuccessChange)),
        );
    }

    public deleteAgent(slrId: number): Observable<void> {
        return this.#agentService.apiAgentRemoveAgentDelete({ slrId }).pipe(
            startWithLoading(this, StartWithUnit.RequestAgentDelete),
            tap(() => this.requestAgents()),
        );
    }

    public createAgentDepartment(body: SetAgentDeptRequest, slrId: number): Observable<void> {
        return this.#agentSitesService.apiAgentAddAgentDeptPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.RequestAgentDepartmentAdd),
            tap(() => this.requestAgentDepartments(slrId)),
        );
    }

    public updateAgentDepartment(body: SetAgentDeptRequest, slrId: number): Observable<void> {
        return this.#agentSitesService.apiAgentSetAgentDeptPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.RequestAgentDepartmentAdd),
            tap(() => this.requestAgentDepartments(slrId)),
        );
    }

    public deleteAgentDepartment(orgId: number, slrId: number): Observable<void> {
        return this.#agentSitesService.apiAgentRemoveAgentDeptDelete({ orgId }).pipe(
            startWithLoading(this, StartWithUnit.RequestAgentDepartmentDelete),
            tap(() => this.requestAgentDepartments(slrId)),
        );
    }

    public agentUploadCards(slrId: number, body: { file?: Blob }): Observable<AddFile> {
        return this.#agentService.apiAgentLoadCardStoreSlrIdPost({ slrId, body }).pipe(
            startWithLoading(this, StartWithUnit.RequestAgentUploadCards),
            tap(() => this.requestAgents()),
        );
    }

    /**
     * Generate url for map loading result
     *
     * @params
     */
    public makeReportUrl(params: AddFile): string {
        return AgentService.ApiAgentListLoadResultGetPath + '?' +
            'lang=' + this.#languageState.currentLang + '&' +
            'status=0' + '&' +
            'fileSid=' + params.loadSid + '&' +
            'fileName=' + params.fileName + '&' +
            'token=' + this.token;
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            agents: null,
            agentDepartments: null,
        });
    }

    private connectSelectors(): void {
        this.connect('agents', this.#actions.requestAgents$);
        this.connect('agentDepartments', this.#actions.requestAgentDepartments$);
    }
}
